import { useState } from "react";
import { Box, Flex, Tab, TabList, TabPanel, TabPanels, Text, useMediaQuery, useTheme } from "@chakra-ui/react";
import { componentMap } from "..";
import { AccessibleSelect } from "@/components/AccessibleSelect/AccessibleSelect";
import { AccessibleTabs } from "@/components/AccessibleTabs/AccessibleTabs";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import { WithTooltip } from "@/components/WithTooltip/WithTooltip";
import type { ShareClassDashboard } from "@/services/server/v1/getShareClassDashboard";
import { htmlify } from "@/services/utils";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";

export interface FxPerformanceGroupProps {
	heading: string;
	subHeading?: string;
	sections: {
		id: string;
		heading: string;
		component: keyof ShareClassDashboard;
		componentProps?: Record<string, unknown>;
	}[];
	noPerformanceDisclaimer?: string;
}

export const FxPerformanceGroupPanel = (props: FundDetailLayoutComponentProps) => {
	const { dashboard } = props;
	const theme = useTheme();
	const [isMobile] = useMediaQuery(`(max-width: ${theme?.breakpoints?.md || 320})`);

	const { fxPerformanceGroupPanel } = dashboard;

	const [section, setSection] = useState(() => {
		return dashboard.fxPerformanceGroupPanel?.sections ? dashboard.fxPerformanceGroupPanel?.sections[0] : undefined;
	});
	const PerfomanceComponent = section ? componentMap[section.component] : null;

	if (!fxPerformanceGroupPanel) {
		return;
	}
	const { heading, sections, subHeading, noPerformanceDisclaimer } = fxPerformanceGroupPanel;

	if (noPerformanceDisclaimer) {
		return (
			<ContentBlock id="fxPerformanceTablePanel" data-testid="FxPerformanceTablePanel" heading={htmlify(heading)}>
				<Flex padding="15px" bg="backgrounds.article">
					{htmlify(noPerformanceDisclaimer)}
				</Flex>
			</ContentBlock>
		);
	}

	return (
		<ContentBlock
			id="fxPerformanceGroupPanel"
			data-testid="FxPerformanceGroupPanel"
			heading={htmlify(heading)}
			subHeading={htmlify(subHeading)}
			tabIndex={-1}
		>
			<Box>
				{isMobile ? (
					<>
						<AccessibleSelect
							id="performance-group-panel"
							aria-label="Select a type"
							variant="brand"
							maxWidth="460px"
							minHeight="50px"
							fontWeight="bold"
							mb={2}
							onChange={(e) => {
								const value = e.target.value;
								const newSection = sections?.[parseInt(value, 10)];

								setSection(newSection);
							}}
						>
							{sections?.map(({ heading }, headingIndex) => {
								return (
									<option key={headingIndex} value={headingIndex}>
										{htmlify(heading)}
									</option>
								);
							})}
						</AccessibleSelect>
						{section && PerfomanceComponent && (
							<PerfomanceComponent
								key={section.id}
								{...{
									...props,
									dashboard: {
										...dashboard,
										[section.component]: section.componentProps
									}
								}}
							/>
						)}
					</>
				) : (
					<AccessibleTabs variant="brandAndGrey" id="performance-group">
						<TabList overflow="visible" marginBottom={2.5}>
							{sections?.map(({ id, heading }, headingIndex) => {
								return (
									<Tab key={headingIndex} data-testid={`tab_${id}`}>
										<WithTooltip>
											<Text variant="largeLabel" fontWeight="bold" overflowWrap="anywhere" whiteSpace="break-spaces">
												{htmlify(heading)}
											</Text>
										</WithTooltip>
									</Tab>
								);
							})}
						</TabList>
						<TabPanels>
							{sections?.map(({ id, component, componentProps }, sectionIndex) => {
								const Component = componentMap[component];

								return (
									<TabPanel
										key={sectionIndex}
										padding={0}
										data-testid={`tabPanel_${id}`}
										_focusVisible={{
											outline: "3px solid #A6CBED",
											outlineOffset: "0px",
											borderRadius: 6
										}}
									>
										{Component && (
											<Component
												{...{
													...props,
													dashboard: {
														...dashboard,
														[component]: componentProps
													}
												}}
											/>
										)}
									</TabPanel>
								);
							})}
						</TabPanels>
					</AccessibleTabs>
				)}
			</Box>
		</ContentBlock>
	);
};
